import { Graphics } from '../../vendor/pixi.min.mjs';

/** Fade half-duration in 60 Hz frames: 7.5 ≈ 125 ms out + 125 ms in = 250 ms total. */
const FADE_FRAMES = 7.5;

/**
 * Manages scene lifecycle: registration, switching, updating, and resize propagation.
 * Only one scene is active at a time.
 *
 * Switches run through a short fade: 125 ms to black, hard swap while
 * fully covered, 125 ms back in — so editor → test-play → editor (and
 * every other transition) stops hard-cutting. The very first switch
 * (boot, nothing on screen yet) skips the fade-out and just fades in.
 */
export class SceneManager {
    /** @type {Map<string, import('../scenes/BaseScene.js').BaseScene>} */
    _scenes = new Map();

    /** @type {import('../scenes/BaseScene.js').BaseScene|null} */
    _activeScene = null;

    /** @type {string|null} */
    _activeSceneName = null;

    /** @type {import('./Game.js').Game} */
    _game = null;

    /** Full-screen black Graphics used for the cross-fade (lazy). */
    _fadeOverlay = null;
    /** 'out' (covering) | 'in' (revealing) | null (idle). */
    _fadePhase = null;
    /** Fade progress in 60 Hz frames, 0..FADE_FRAMES within the phase. */
    _fadeClock = 0;
    /** Scene name to swap to once the screen is fully covered. */
    _fadeTarget = null;

    /**
     * @param {import('./Game.js').Game} game
     */
    constructor(game) {
        this._game = game;
        this._game.onUpdate((delta) => this.update(delta));
    }

    /**
     * Register a scene by name. Does NOT activate it.
     * @param {string} name
     * @param {import('../scenes/BaseScene.js').BaseScene} scene
     */
    addScene(name, scene) {
        this._scenes.set(name, scene);
    }

    /**
     * Fade to black, swap scenes, fade back in. Calling again while a
     * transition is in flight just RETARGETS it (last call wins) — the
     * swap itself happens exactly once, behind full cover.
     * @param {string} name
     */
    switchScene(name) {
        if (!this._scenes.has(name)) {
            console.error(`[SceneManager] Unknown scene: "${name}"`);
            return;
        }
        if (this._fadePhase === 'out') {
            this._fadeTarget = name;        // retarget the pending swap
            return;
        }
        if (this._fadePhase === 'in') {
            // Already revealing the previous target — turn around and
            // cover again toward the new one, keeping alpha continuous.
            this._fadeTarget = name;
            this._fadePhase = 'out';
            this._fadeClock = (1 - this._fadeClock / FADE_FRAMES) * FADE_FRAMES;
            return;
        }
        if (!this._activeScene) {
            // Boot: nothing on screen to fade away — swap now, fade in.
            this._doSwitch(name);
            this._beginFade('in');
            return;
        }
        this._fadeTarget = name;
        this._beginFade('out');
    }

    /**
     * The original hard swap: tear down the outgoing scene, init the
     * incoming one. Runs while the fade overlay fully covers the stage.
     */
    _doSwitch(name) {
        const next = this._scenes.get(name);
        if (!next) {
            console.error(`[SceneManager] Unknown scene: "${name}"`);
            return;
        }

        // Detach the outgoing scene FIRST so that if `destroy()` throws,
        // the broken scene is no longer the `_activeScene` and the per-frame
        // update loop can't keep ticking it (which would cascade further
        // errors and freeze the game).
        const prev = this._activeScene;
        this._activeScene = null;
        this._activeSceneName = null;
        if (prev) {
            if (prev.container?.parent) {
                this._game.stage.removeChild(prev.container);
            }
            try {
                prev.destroy();
            } catch (err) {
                console.error(`[SceneManager] destroy() threw while leaving scene:`, err);
            }
        }

        this._activeScene = next;
        this._activeSceneName = name;

        next.init(this._game);
        this._game.stage.addChild(next.container);

        console.log(`[SceneManager] Switched to "${name}"`);
    }

    _beginFade(phase) {
        this._fadePhase = phase;
        this._fadeClock = 0;
        const overlay = this._ensureOverlay();
        overlay.alpha = phase === 'out' ? 0 : 1;
        overlay.visible = true;
        // Re-add so the overlay sits above whichever scene container
        // was most recently attached to the stage.
        this._game.stage.addChild(overlay);
    }

    _ensureOverlay() {
        if (this._fadeOverlay) return this._fadeOverlay;
        const g = new Graphics();
        g.rect(0, 0, this._game.width, this._game.height);
        g.fill(0x000000);
        g.eventMode = 'none';
        g.visible = false;
        this._fadeOverlay = g;
        return g;
    }

    /** Called every frame by Game */
    update(delta) {
        if (this._fadePhase) this._stepFade(delta);
        if (this._activeScene) {
            this._activeScene.update(delta);
        }
    }

    _stepFade(delta) {
        // Clamp so a long hitch (tab wake, big GC) can't skip the swap
        // frame or overshoot into negative alpha.
        this._fadeClock = Math.min(FADE_FRAMES, this._fadeClock + Math.max(0, delta));
        const t = this._fadeClock / FADE_FRAMES;            // 0 → 1
        const overlay = this._ensureOverlay();

        if (this._fadePhase === 'out') {
            overlay.alpha = t;
            if (t >= 1) {
                // Fully covered — do the hard swap, then reveal.
                const target = this._fadeTarget;
                this._fadeTarget = null;
                if (target) this._doSwitch(target);
                this._beginFade('in');
            }
        } else if (this._fadePhase === 'in') {
            overlay.alpha = 1 - t;
            if (t >= 1) {
                overlay.visible = false;
                this._fadePhase = null;
                this._fadeClock = 0;
            }
        }
    }

    /** @returns {string|null} Name of the currently active scene */
    get activeSceneName() {
        return this._activeSceneName;
    }
}
